import { useState, useMemo, useEffect } from 'react'
import { ChevronDown, ChevronUp, Waypoints, Radio } from 'lucide-react'
import { parseDsl } from '@features/dashboards/components/ClientSequenceDiagram/dsl-parser'
import { SequenceDiagram } from '@features/dashboards/components/ClientSequenceDiagram/SequenceDiagram'

interface InteractionDiagramProps {
  stageId: string
  dsl: string
  /**
   * When set, highlights whichever top-level step's label matches this pattern and
   * auto-expands the panel — used to make the diagram "come alive" while a live call
   * (e.g. IDV capture) is actually running. Omit for the normal static reference view.
   */
  activeStepMatch?: RegExp | null
}

export function InteractionDiagram({ stageId, dsl, activeStepMatch = null }: InteractionDiagramProps) {
  const [open, setOpen] = useState(true)
  const { flow, errors } = useMemo(() => parseDsl(stageId, dsl), [stageId, dsl])
  const activeIndex = useMemo(() => {
    if (!activeStepMatch) return null
    const idx = flow.steps.findIndex((s) => 'label' in s && activeStepMatch.test(s.label))
    return idx === -1 ? null : idx
  }, [flow, activeStepMatch])

  // Force the panel open the moment a live call starts driving it, so the user doesn't
  // have to remember to expand "How it works" themselves to see it react.
  useEffect(() => {
    if (activeStepMatch) setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!activeStepMatch])

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <Waypoints size={12} className="text-[#4f46e5]" />
          How it works — {flow.title}
          {activeStepMatch && (
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,.15)', color: '#4f46e5' }}>
              <Radio size={8} className="animate-pulse" /> Live
            </span>
          )}
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>
      {open && (
        <div className="p-3">
          <SequenceDiagram flow={flow} activeIndex={activeIndex} />
          {errors.length > 0 && (
            <p className="mt-2 text-[10px] text-red-700">{errors.join('; ')}</p>
          )}
        </div>
      )}
    </div>
  )
}
